/**
 * LINE Bot > 設定タブ — LINE Bot の挙動関連設定を集約。
 *
 * 2026-07-27: システム設定 > 一般設定の「AI モデル / セッション」セクションから移設。
 * 保存先は従来どおり settings/general（functions 側の参照は変更なし）。
 */
import { CLAUDE_MODELS, LINE_ROUTING_MODES, type LineRoutingMode } from '@goenhub/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useGeneralSettings, useUpdateGeneralSettings } from '@/hooks/useSettings';

const schema = z.object({
  claudeModel: z.string(),
  routingMode: z.enum(LINE_ROUTING_MODES),
  sessionTimeoutMinutes: z.coerce.number().int().positive(),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  claudeModel: CLAUDE_MODELS.sonnetDefault,
  routingMode: 'router',
  sessionTimeoutMinutes: 60,
};

const ROUTING_MODE_LABELS: Record<LineRoutingMode, { label: string; desc: string }> = {
  haiku: {
    label: 'Haiku 固定（最速）',
    desc: '全 round で Haiku を使用。シンプルな問合せ向け、~3-8秒で回答',
  },
  sonnet: {
    label: 'Sonnet 固定（高品質）',
    desc: '全 round で Sonnet を使用。従来挙動、~30-50秒で回答',
  },
  router: {
    label: 'ルーター（推奨：Haiku + Sonnet 自動切替）',
    desc: '初回 round は Haiku、tool 結果の合成 round で Sonnet。バランス型、~10-20秒で回答',
  },
};

export function LineBotSettingsTab() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data, isLoading } = useGeneralSettings();
  const updateMutation = useUpdateGeneralSettings();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (data) {
      form.reset({
        claudeModel: data.claudeModel ?? CLAUDE_MODELS.sonnetDefault,
        routingMode: data.routingMode ?? 'router',
        sessionTimeoutMinutes: data.sessionTimeoutMinutes ?? 60,
      });
    }
  }, [data, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      // settings/general への部分 merge 保存（他のフィールドには影響しない）
      await updateMutation.mutateAsync(values);
      toast.success('LINE Bot 設定を保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-bold">LINE Bot 設定</h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          AI 応答モデルとセッションの挙動を設定します
        </p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-2xl space-y-6">
        <section>
          <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
            AI モデル
          </h3>
          <div className="space-y-4">
            <div>
              <Label>ルーティング戦略</Label>
              <Select
                value={form.watch('routingMode')}
                onValueChange={(v) => form.setValue('routingMode', v as LineRoutingMode)}
                disabled={!isOwner}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LINE_ROUTING_MODES.map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {ROUTING_MODE_LABELS[mode].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">
                {ROUTING_MODE_LABELS[form.watch('routingMode')]?.desc}
                <br />
                ※ ルーター/Haiku モードでは下の「Claude モデル」設定は無視されます
              </p>
            </div>
            <div>
              <Label>Claude モデル（フォールバック用）</Label>
              <Select
                value={form.watch('claudeModel')}
                onValueChange={(v) => form.setValue('claudeModel', v)}
                disabled={!isOwner}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CLAUDE_MODELS.sonnetDefault}>
                    {CLAUDE_MODELS.sonnetDefault} (デフォルト)
                  </SelectItem>
                  <SelectItem value={CLAUDE_MODELS.opus}>{CLAUDE_MODELS.opus}</SelectItem>
                  <SelectItem value={CLAUDE_MODELS.haiku}>{CLAUDE_MODELS.haiku}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                ルーティング戦略が未設定（後方互換）の場合のみ使用
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
            セッション
          </h3>
          <div>
            <Label htmlFor="sessionTimeoutMinutes">セッションタイムアウト（分）</Label>
            <Input
              id="sessionTimeoutMinutes"
              type="number"
              min={1}
              className="max-w-[200px]"
              {...form.register('sessionTimeoutMinutes')}
              disabled={!isOwner}
            />
            {form.formState.errors.sessionTimeoutMinutes && (
              <p className="text-xs text-[var(--accent-red)] mt-1">
                {form.formState.errors.sessionTimeoutMinutes.message}
              </p>
            )}
            <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">
              最終メッセージからこの時間を超えた会話中セッションを自動クローズします
              （10 分ごとにチェック）。クローズ後の次メッセージは新しいセッションとして開始され、
              前の話題（コンテキスト）を引き継ぎません。
            </p>
          </div>
        </section>

        {isOwner && (
          <div className="flex justify-end gap-2 pt-4 border-t border-[var(--border)]">
            <Button
              type="button"
              variant="secondary"
              onClick={() => data && form.reset()}
              disabled={updateMutation.isPending}
            >
              取り消し
            </Button>
            <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
